import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { usePortalSlot } from '../hooks/usePortalSlot';
import { promptApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import AnalysisProfileSelect from '../components/AnalysisProfileSelect';
import DimensionCard, { DimensionDataItem } from '../components/media-planning/DimensionCard';
import FilterBar from '../components/media-planning/FilterBar';
import BlueprintComparison from '../components/media-planning/BlueprintComparison';
import InventoryOverviewPanel from '../components/media-planning/InventoryOverviewPanel';
import AIInsightsPanel, { ContentInsight } from '../components/media-planning/AIInsightsPanel';
import HistoricalTrendsChart from '../components/media-planning/HistoricalTrendsChart';
import { useMediaPlanningData, useComparisonData, useRecommendations, useHistoricalTrends } from '../hooks/useMediaPlanningData';
import { useSelectedBlueprint } from '../hooks/useSelectedBlueprint';

const MediaPlanning: React.FC = () => {
    const { user } = useAuth();
    const { selectedCompanyId } = useCompany();
    const [storedBlueprintId, setStoredBlueprintId, clearStoredBlueprint] = useSelectedBlueprint();
    // Seed the local selection from the persisted blueprint id on mount so the
    // choice survives reloads and is shared with the other blueprint-aware pages.
    const [selectedPromptId, setSelectedPromptId] = useState<string | null>(() => storedBlueprintId || null);
    const [searchParams, setSearchParams] = useSearchParams();
    // Seed the comparison with the currently-selected blueprint so the user
    // only needs to add one more to see the table.
    const [comparisonIds, setComparisonIds] = useState<string[]>(() => (selectedPromptId ? [selectedPromptId] : []));
    const [availableBlueprints, setAvailableBlueprints] = useState<Array<{ id: string; name: string }>>([]);
    // Mirrors ScheduledJobsPage: the toolbar row portals into AppLayout's
    // shared app-page-actions slot so this page's first content row aligns
    // with Brand Blueprints and Scheduled Jobs.
    const actionsSlot = usePortalSlot('app-page-actions');
    // The Brand Blueprint field is first on the shared header row, captioned
    // and accented, mounted into AppLayout's left context slot
    // (deepcontext-tpe1) — everything else stays on the right, unmoved.
    const contextSlot = usePortalSlot('app-page-context');

    // Same source the header AnalysisProfileSelect uses, for the comparison
    // card's Add-Blueprint selector — including the same eskimi_admin/adops
    // company scoping, so both selectors always agree on which company's
    // blueprints are in scope.
    useEffect(() => {
        let cancelled = false;
        const companyIdToUse =
            user?.role === 'eskimi_admin' || user?.role === 'adops' ? selectedCompanyId || undefined : undefined;
        promptApi
            .getAll(companyIdToUse)
            .then((prompts) => {
                if (cancelled) return;
                setAvailableBlueprints(prompts.map((p) => ({ id: p.id, name: p.name })));
            })
            .catch(() => {
                if (cancelled) return;
                setAvailableBlueprints([]);
            });
        return () => {
            cancelled = true;
        };
    }, [user?.role, selectedCompanyId]);

    // Persist every selection change through to localStorage while keeping the
    // local state authoritative for this page's data fetching.
    const handleSelectBlueprint = useCallback((id: string | null) => {
        setSelectedPromptId(id);
        setStoredBlueprintId(id ?? '');
        // Seed the comparison with the newly-picked blueprint the same way the
        // initial page-load does, but only when nothing is being compared yet —
        // an in-progress comparison should not be clobbered by a header change.
        if (id) {
            setComparisonIds((prev) => (prev.length === 0 ? [id] : prev));
        }
    }, [setStoredBlueprintId]);

    // When the persisted blueprint no longer resolves (deleted/inaccessible),
    // clear storage and reset the page to the empty "Select a brand blueprint"
    // state.
    const handleInvalidSelection = useCallback(() => {
        clearStoredBlueprint();
        setSelectedPromptId(null);
    }, [clearStoredBlueprint]);

    const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);

    const { overview, dimensions, insights, loading, error } = useMediaPlanningData(selectedPromptId, filters);
    const { trends, trendsLoading } = useHistoricalTrends(selectedPromptId);
    const { comparisonData, comparisonLoading } = useComparisonData(comparisonIds);
    const { recommendations, recommendationsLoading, generate, dismiss } =
        useRecommendations(selectedPromptId, overview, filters, dimensions);

    const normalizedDimensions = useMemo(() => normalizeDimensions(dimensions), [dimensions]);

    const handleFilterClick = useCallback((dimension: string, value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(dimension, value);
            return next;
        });
    }, [setSearchParams]);

    const handleRemoveFilter = useCallback((key: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete(key);
            return next;
        });
    }, [setSearchParams]);

    const handleClearAll = useCallback(() => {
        setSearchParams({});
    }, [setSearchParams]);

    const handleAddBlueprint = useCallback((id: string) => {
        setComparisonIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }, []);

    const handleRemoveBlueprint = useCallback((id: string) => {
        setComparisonIds((prev) => prev.filter((x) => x !== id));
    }, []);

    return (
        <div className="space-y-6">
            {error && <ErrorBanner message={error} />}
            {contextSlot &&
                createPortal(
                    <BlueprintSelector
                        selectedPromptId={selectedPromptId}
                        onSelect={handleSelectBlueprint}
                        onInvalidSelection={handleInvalidSelection}
                    />,
                    contextSlot,
                )}
            {actionsSlot &&
                createPortal(
                    <div className="flex flex-wrap items-center justify-between gap-3" data-testid="media-planning-toolbar">
                        <FilterBar
                            filters={filters}
                            onRemoveFilter={handleRemoveFilter}
                            onClearAll={handleClearAll}
                        />
                    </div>,
                    actionsSlot,
                )}
            {!selectedPromptId ? (
                <EmptyState />
            ) : (
                <BlueprintContent
                    overview={overview}
                    dimensions={normalizedDimensions}
                    trends={trends}
                    trendsLoading={trendsLoading}
                    insights={insights}
                    recommendations={recommendations}
                    comparisonData={comparisonData}
                    comparisonIds={comparisonIds}
                    availableBlueprints={availableBlueprints}
                    loading={loading}
                    recommendationsLoading={recommendationsLoading}
                    comparisonLoading={comparisonLoading}
                    onFilterClick={handleFilterClick}
                    onDismissRecommendation={dismiss}
                    onGenerateRecommendations={generate}
                    onAddBlueprint={handleAddBlueprint}
                    onRemoveBlueprint={handleRemoveBlueprint}
                />
            )}
        </div>
    );
};

function normalizeDimensions(
    dimensions: Array<{ title: string; dimension: string; items: Array<{ value: string; rawValue?: string; impressions: number; uniqueUrls?: number; percentage: number }> }>,
): Array<{ title: string; dimension: string; data: DimensionDataItem[] }> {
    return dimensions.map((d) => ({
        title: d.title,
        dimension: d.dimension,
        data: d.items.map((item) => ({ ...item, uniqueUrls: item.uniqueUrls ?? 0 })),
    }));
}

const INSIGHT_META: Record<string, string> = {
    themes: 'Top 10 Content Themes',
    trending: 'Top 10 Trending Topics',
    sentiment: 'Sentiment Distribution',
    quality: 'Top 10 Content Quality',
};

function normalizeInsights(raw: any): ContentInsight[] {
    if (Array.isArray(raw)) return raw;
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(INSIGHT_META)
        .filter(([key]) => Array.isArray(raw[key]) && raw[key].length > 0)
        .map(([key, title]) => ({
            type: key as ContentInsight['type'],
            title,
            items: (raw[key] as any[]).map((i) => ({
                label: i.value ?? i.label ?? '',
                value: i.count ?? i.value ?? 0,
                percentage: i.percentage,
            })),
        }));
}

function paramsToFilters(params: URLSearchParams): Record<string, string> {
    const filters: Record<string, string> = {};
    params.forEach((value, key) => {
        filters[key] = value;
    });
    return filters;
}

function BlueprintSelector({
    selectedPromptId,
    onSelect,
    onInvalidSelection,
}: {
    selectedPromptId: string | null;
    onSelect: (id: string | null) => void;
    onInvalidSelection: () => void;
}) {
    return (
        <AnalysisProfileSelect
            value={selectedPromptId ?? ''}
            onChange={(id) => onSelect(id || null)}
            onInvalidSelection={onInvalidSelection}
            className="w-56"
            triggerClassName="h-[42px]"
            captioned
        />
    );
}

function ErrorBanner({ message }: { message: string }) {
    return (
        <div className="bg-risk-bg border border-risk rounded-2xl p-4 text-risk">
            <span className="font-medium">Error:</span> {message}
        </div>
    );
}

function EmptyState() {
    return (
        <div className="bg-surface rounded-2xl shadow-sm border border-line p-12 text-center">
            <p className="text-ink-muted">Select a brand blueprint above to view media planning data.</p>
        </div>
    );
}

interface BlueprintContentProps {
    overview: any;
    dimensions: Array<{ title: string; dimension: string; data: DimensionDataItem[] }>;
    trends: Array<{ date: string; totalAvails: number; brandSafeUrls: number }>;
    trendsLoading: boolean;
    insights: any;
    recommendations: any[];
    comparisonData: any[];
    comparisonIds: string[];
    availableBlueprints: Array<{ id: string; name: string }>;
    loading: boolean;
    recommendationsLoading: boolean;
    comparisonLoading: boolean;
    onFilterClick: (dimension: string, value: string) => void;
    onDismissRecommendation: (id: string) => void;
    onGenerateRecommendations: () => void;
    onAddBlueprint: (id: string) => void;
    onRemoveBlueprint: (id: string) => void;
}

function BlueprintContent(props: BlueprintContentProps) {
    return (
        <>
            <InventoryOverviewPanel
                totalImpressions={props.overview?.totalImpressions ?? 0}
                totalImpressionsUnavailable={props.overview != null && props.overview.totalImpressions == null}
                brandSafeUrls={props.overview?.brandSafeUrls ?? 0}
                matchRate={props.overview?.matchRate ?? 0}
                lastUpdated={props.overview?.lastUpdated ?? null}
                loading={props.loading}
            />
            <SentimentTrendsRow
                sentiment={props.dimensions.find((d) => d.dimension === SENTIMENT_DIMENSION)}
                trends={props.trends}
                trendsLoading={props.trendsLoading}
                loading={props.loading}
                onFilterClick={props.onFilterClick}
            />
            <DimensionCardsGrid
                dimensions={props.dimensions.filter((d) => d.dimension !== SENTIMENT_DIMENSION)}
                loading={props.loading}
                onFilterClick={props.onFilterClick}
            />
            <AIInsightsPanel
                insights={normalizeInsights(props.insights)}
                recommendations={props.recommendations}
                onDismissRecommendation={props.onDismissRecommendation}
                onGenerateRecommendations={props.onGenerateRecommendations}
                loading={props.loading}
                recommendationsLoading={props.recommendationsLoading}
            />
            <BlueprintComparison
                data={props.comparisonData}
                availableBlueprints={props.availableBlueprints}
                selectedBlueprintIds={props.comparisonIds}
                onAddBlueprint={props.onAddBlueprint}
                onRemoveBlueprint={props.onRemoveBlueprint}
                loading={props.comparisonLoading}
            />
        </>
    );
}

// The sentiment donut is small enough to sit beside Availability Trends
// rather than eat a full dimension-grid cell, so the two share one row: donut
// on the left third, trends on the right two thirds, stacked on small screens.
const SENTIMENT_DIMENSION = 'sentiment';

interface SentimentTrendsRowProps {
    sentiment?: { title: string; dimension: string; data: DimensionDataItem[] };
    trends: Array<{ date: string; totalAvails: number; brandSafeUrls: number }>;
    trendsLoading: boolean;
    loading: boolean;
    onFilterClick: (dimension: string, value: string) => void;
}

function SentimentTrendsRow({ sentiment, trends, trendsLoading, loading, onFilterClick }: SentimentTrendsRowProps) {
    return (
        <div data-testid="sentiment-trends-row" className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            {sentiment && (
                <div>
                    <DimensionCard
                        title={sentiment.title}
                        dimension={sentiment.dimension}
                        data={sentiment.data}
                        loading={loading}
                        onFilterClick={onFilterClick}
                        chartType="donut"
                    />
                </div>
            )}
            <div className={sentiment ? 'lg:col-span-2' : 'lg:col-span-3'}>
                <HistoricalTrendsChart data={trends} loading={trendsLoading} />
            </div>
        </div>
    );
}

interface DimensionCardsGridProps {
    dimensions: Array<{ title: string; dimension: string; data: DimensionDataItem[] }>;
    loading: boolean;
    onFilterClick: (dimension: string, value: string) => void;
}

function DimensionCardsGrid({ dimensions, loading, onFilterClick }: DimensionCardsGridProps) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {dimensions.map((dim) => (
                <DimensionCard
                    key={dim.dimension}
                    title={dim.title}
                    dimension={dim.dimension}
                    data={dim.data}
                    loading={loading}
                    onFilterClick={onFilterClick}
                    chartType="bar"
                />
            ))}
        </div>
    );
}

export default MediaPlanning;
